import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@iplan/auth/react';
import axiosInstance from '@/lib/axios';
import { endpoints } from '@/api/endpoints';
import type { AppPermissions, Feature } from '@/api/types';

const DEFAULT_PERMISSIONS: AppPermissions = {
  features: [],
  hasPermissions: () => false,
  hasPermission: () => false,
};

interface PermissionsProviderProps {
  children: ReactNode;
}

const PermissionsContext = createContext<AppPermissions | undefined>(undefined);

const PERMISSIONS_CACHE_KEY = 'iplan.web.permissions';

async function getPermissions(userId: string): Promise<AppPermissions> {
  const response = await axiosInstance.get<{ features: Feature[] }>(
    `${endpoints.permissions.base}/${encodeURIComponent(userId)}`,
  );
  return {
    features: response.data?.features ?? [],
    hasPermission: () => false,
    hasPermissions: () => false,
  };
}

function readCachedPermissions(): AppPermissions | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage.getItem(PERMISSIONS_CACHE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { features?: Feature[] };
    return {
      features: parsed.features ?? [],
      hasPermission: () => false,
      hasPermissions: () => false,
    };
  } catch {
    return undefined;
  }
}

function writeCachedPermissions(value: AppPermissions | undefined): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === undefined) {
      window.localStorage.removeItem(PERMISSIONS_CACHE_KEY);
    } else {
      // Only persist the serialisable bits.
      window.localStorage.setItem(
        PERMISSIONS_CACHE_KEY,
        JSON.stringify({ features: value.features }),
      );
    }
  } catch {
    // Storage may be unavailable.
  }
}

export const usePermissionsContext = (): AppPermissions => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissionsContext must be used within a PermissionsProvider');
  }

  const hasPermission = useCallback(
    (name: string): boolean => context.features.some((feature) => feature.name === name),
    [context.features],
  );

  const hasPermissions = useCallback(
    (requiredPermissions: string[]): boolean =>
      requiredPermissions.some((name) => hasPermission(name)),
    [hasPermission],
  );

  return useMemo(
    () => ({ ...context, hasPermission, hasPermissions }),
    [context, hasPermission, hasPermissions],
  );
};

export const PermissionsProvider = ({ children }: PermissionsProviderProps): ReactNode => {
  const auth = useAuth();
  const userId = auth.user?.profile?.sub;
  const [cachedPermissions, setCachedPermissions] = useState<AppPermissions | undefined>(() =>
    readCachedPermissions(),
  );

  const { data, isLoading, error } = useQuery<AppPermissions>({
    queryKey: ['permissionsData', userId],
    queryFn: async () => {
      if (!userId) return DEFAULT_PERMISSIONS;
      return getPermissions(userId);
    },
    refetchOnWindowFocus: false,
    retry: typeof process !== 'undefined' && process.env?.NODE_ENV === 'test' ? 0 : 10,
    retryDelay: (retryAttempt: number) => Math.min(1000 * 2 ** retryAttempt, 30000),
    // Fire as soon as we know who the user is. (The previous
    // `auth.isAuthenticated && …` gate also required the AuthProvider's status
    // to have flipped to 'authenticated'; a present `sub` already implies a
    // user, and gating on the boolean meant the fetch silently never ran.)
    enabled: !!userId,
    staleTime: 12 * 60 * 60 * 1000,
    gcTime: 12 * 60 * 60 * 1000,
    refetchOnMount: 'always',
    refetchInterval: 30 * 60 * 1000,
    refetchIntervalInBackground: true,
    // placeholderData (not initialData) so the cached set renders instantly but
    // the query is NOT treated as already-fresh — it always fetches on mount.
    placeholderData: cachedPermissions,
  });

  useEffect(() => {
    if (data) {
      setCachedPermissions(data);
      writeCachedPermissions(data);
    }
  }, [data]);

  if (isLoading && !cachedPermissions) {
    return (
      <div className="flex h-full items-center justify-center p-12">
        <div
          className="h-6 w-6 animate-spin rounded-full border-2 border-iplan-brand border-t-transparent"
          aria-label="Loading Permissions..."
        />
      </div>
    );
  }

  if (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching permissions:', error);
    return (
      <>
        <div>Error: {(error as Error).message}</div>
        {children}
      </>
    );
  }

  return (
    <PermissionsContext.Provider value={data ?? cachedPermissions ?? DEFAULT_PERMISSIONS}>
      {children}
    </PermissionsContext.Provider>
  );
};
